import Layout from "Layout";
import { ArrowLeft, ArrowRight, ArrowRightFromLineIcon, Briefcase, LayoutDashboard, Users } from "lucide-react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import TeamDashboardTabV2 from "./components/TeamDashboardTabV2";
import TeamAccountsTab from "./components/TeamAccountsTab";
import TeamMembersTab from "./components/TeamMembersTab";
import { TeamDetailTab } from "./type";
import "./style/index.scss";
import { getMyTeam, leaveTeam } from "./funtion";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Modal } from "antd";

const TEAM_DETAIL_TABS: TeamDetailTab[] = ["dashboard", "accounts", "members"];

const TeamDetails: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const teamId = Number(id);

  // Lấy isAdmin từ location.state, sử dụng optional chaining (?.) để tránh lỗi nếu state bị undefined
  const [isAdmin, setIsAdmin] = useState<boolean | undefined>(location.state?.isAdmin);
  const [canLeave, setCanLeave] = useState<boolean | undefined>(location.state?.canLeave);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showleaveConfirmModal, setShowLeaveConfirmModal] = useState(false);

  useEffect(() => {
    const fetchRoleInfo = async () => {
      if (!teamId) return;
      try {
        const myTeams = await getMyTeam();
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const myId = user.id;

        const team = myTeams.find(t => t.team_id === teamId);
        if (team) {
          const isUserAdmin = team.members?.some((member) => member.user_id === myId && (member.role === "Admin" || member.role === "Deputy"));
          const userCanLeave = team.members?.some((member) => member.user_id === myId && member.role !== "Admin");
          setIsAdmin(isUserAdmin);
          setCanLeave(userCanLeave);
        }
      } catch (error) {
        console.error("Error fetching team role info:", error);
      }
    };

    if (isAdmin === undefined || canLeave === undefined) {
      fetchRoleInfo();
    }
  }, [teamId, isAdmin, canLeave]);

  const activeTab: TeamDetailTab = TEAM_DETAIL_TABS.includes(
    searchParams.get("tab") as TeamDetailTab
  )
    ? (searchParams.get("tab") as TeamDetailTab)
    : isAdmin
      ? "dashboard"
      : "accounts";

  const handleTabChange = (tab: TeamDetailTab) => {

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    }, { state: { isAdmin, canLeave } });
  };

  return (
    <Layout>
      <>
        <Helmet>
          <title>
            {t("Teams")} | LUMIR
          </title>
        </Helmet>
        <div className="flex items-center justify-between mb-4">
          <nav className="breadcrumb__container mb-3">
            <div
              className="breadcrumb__gradient breadcrumb__gradient--left"
              data-gradient="left"
              style={{ display: "none" }}
            ></div>
            <ul className="breadcrumb" data-breadcrumb-scroll-container="">
              <li className="breadcrumb-item">
                <a href="/teams">{t("Teams_dashboard.navigation.teams")}</a>
              </li>
              <li className="breadcrumb-item active">
                {t("Teams_dashboard.navigation.detail_team")}
              </li>
            </ul>
            <div
              className="breadcrumb__gradient breadcrumb__gradient--right"
              data-gradient="right"
              style={{ display: "none" }}
            ></div>
          </nav>
          { canLeave && (
            <button type="button" className="flex items-center gap-1.5 px-4 py-2.5 !text-sm !text-red-600 border border-red-600 rounded hover:bg-red-50 transition-colors hover:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              onClick={async () => {
                setShowLeaveConfirmModal(true);
              }}>
              <ArrowRightFromLineIcon size={16} />
              {t('13W_Dashboard.LeaveTeam')}
            </button>
          )}
        </div>

        <div className="team-detail-tabs flex mb-4 overflow-x-auto no-scrollbar">
          {
            isAdmin && (
              <button
                type="button"
                onClick={() => handleTabChange("dashboard")}
                className={`team-detail-tabs__tab flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-transparent ${activeTab === "dashboard" ? "team-detail-tabs__tab--active" : ""
                  }`}
              >
                <LayoutDashboard size={16} />
                {t("Dashboard")}
              </button>
            )
          }
          <button
            type="button"
            onClick={() => handleTabChange("accounts")}
            className={`team-detail-tabs__tab flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-transparent ${activeTab === "accounts" ? "team-detail-tabs__tab--active" : ""
              }`}
          >
            <Briefcase size={16} />
            {t("TradingAccountsList")}
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("members")}
            className={`team-detail-tabs__tab flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-transparent ${activeTab === "members" ? "team-detail-tabs__tab--active" : ""
              }`}
          >
            <Users size={16} />
            {t("TeamMembersList")}
          </button>
        </div>
        <Modal
          title={t("13W_Dashboard.LeaveTeamConfirm_title")}
          open={showleaveConfirmModal}
          onCancel={() => setShowLeaveConfirmModal(false)}
          footer={null}
          centered
          className="team-members-tab__confirm-modal"
        >
          <p className="team-members-tab__confirm-text text-sm mb-4">
            {t("13W_Dashboard.LeaveTeamConfirm_message")}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowLeaveConfirmModal(false)}
              className="team-members-tab__confirm-btn team-members-tab__confirm-btn--cancel px-4 py-2 rounded-md text-sm"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={
                async () => {
                  if (teamId) {
                    const data = await leaveTeam(teamId);
                    if (data.success) {
                      setShowLeaveConfirmModal(false);
                      toast.success(t("LeaveTeamSuccess"));
                      await new Promise((resolve) => setTimeout(resolve, 3000)); // Wait for 3 seconds before redirecting
                      window.location.href = "/teams";
                    }
                  }
                }
              }
              className={`team-members-tab__confirm-btn px-4 py-2 rounded-md text-sm text-white team-members-tab__confirm-btn--reject`}
            >
              {t("13W_Dashboard.confirm")}
            </button>
          </div>
        </Modal>


        {
          activeTab === "dashboard" && isAdmin && (
            <TeamDashboardTabV2 />
          )
        }
        {activeTab === "accounts" && <TeamAccountsTab />}
        {activeTab === "members" && <TeamMembersTab />}

      </>
    </Layout>
  );
};

export default TeamDetails;
